<template>
  <div class="team-info">
    <div class="lside">
      <img
          :alt="title"
          :src="logo ? getImageFromProxy(logo) : '/img/team-card-placeholder.png'"
      />
    </div>
    <div class="rside">
      <div class="rside__header">
        <h1>{{ title }}</h1>
        <div class="socials hm">
          <a
              v-if="social?.twitter"
              :href="social?.twitter"
              aria-label="Twitter"
              class="btn btn-primary"
              rel="noopener nofollow"
              target="_blank"
          >
            <svg height="22" width="22">
              <use xlink:href="/icons/sprite.svg#i-twitter"></use>
            </svg>
          </a>
          <a
              v-if="social?.instagram"
              :href="social?.instagram"
              aria-label="Instagram"
              class="btn btn-primary"
              rel="noopener nofollow"
              target="_blank"
          >
            <svg height="22" width="22">
              <use xlink:href="/icons/sprite.svg#i-instagram-2"></use>
            </svg>
          </a>
          <a
              v-if="social?.twitch"
              :href="social?.twitch"
              aria-label="Twitch"
              class="btn btn-primary"
              rel="noopener nofollow"
              target="_blank"
          >
            <svg height="22" width="22">
              <use xlink:href="/icons/sprite.svg#i-twitch"></use>
            </svg>
          </a>
          <a
              v-if="social?.youtube"
              :href="social?.youtube"
              aria-label="Youtube"
              class="btn btn-primary"
              rel="noopener nofollow"
              target="_blank"
          >
            <svg height="22" width="22">
              <use xlink:href="/icons/sprite.svg#i-youtube"></use>
            </svg>
          </a>
          <a
              v-if="social?.steam"
              :href="social?.steam"
              aria-label="Steam"
              class="btn btn-primary"
              rel="noopener nofollow"
              target="_blank"
          >
            <svg height="22" width="22">
              <use xlink:href="/icons/sprite.svg#i-steam-circle-2"></use>
            </svg>
          </a>
        </div>
      </div>
      <p class="desc">
        <span class="ht hd">{{
            isExpanded ? description : description.slice(0, (currW / 3) || 120) + '...'
          }}</span>
        <span class="hm">{{ description }}</span>

        <button
            v-if="description.length > currW / 3"
            class="btn btn-link ht hd"
            @click="isExpanded = !isExpanded"
        >
          {{ isExpanded ? t('show_less') : t('show_more') }}
        </button>
      </p>

      <div class="socials ht hd">
        <a
            v-if="social?.twitter"
            :href="social?.twitter"
            aria-label="Twitter"
            class="btn btn-primary"
            rel="noopener nofollow"
            target="_blank"
        >
          <svg height="22" width="22">
            <use xlink:href="/icons/sprite.svg#i-twitter"></use>
          </svg>
        </a>
        <a
            v-if="social?.instagram"
            :href="social?.instagram"
            aria-label="Instagram"
            class="btn btn-primary"
            rel="noopener nofollow"
            target="_blank"
        >
          <svg height="22" width="22">
            <use xlink:href="/icons/sprite.svg#i-instagram-2"></use>
          </svg>
        </a>
        <a
            v-if="social?.twitch"
            :href="social?.twitch"
            aria-label="Twitch"
            class="btn btn-primary"
            rel="noopener nofollow"
            target="_blank"
        >
          <svg height="22" width="22">
            <use xlink:href="/icons/sprite.svg#i-twitch"></use>
          </svg>
        </a>
        <a
            v-if="social?.youtube"
            :href="social?.youtube"
            aria-label="Youtube"
            class="btn btn-primary"
            rel="noopener nofollow"
            target="_blank"
        >
          <svg height="22" width="22">
            <use xlink:href="/icons/sprite.svg#i-youtube"></use>
          </svg>
        </a>
        <a
            v-if="social?.steam"
            :href="social?.steam"
            aria-label="Steam"
            class="btn btn-primary"
            rel="noopener nofollow"
            target="_blank"
        >
          <svg height="22" width="22">
            <use xlink:href="/icons/sprite.svg#i-steam-circle-2"></use>
          </svg>
        </a>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {ProPlayerSocial} from "~/graphql/generated/schema-types";

const props = defineProps<{
  description: string,
  title: string,
  logo: string,
  social: ProPlayerSocial
}>()
const {t} = useI18n()
const currW = useResize()

const isExpanded = ref(false)
</script>

<style lang="scss" scoped>


.team-info {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 3.2rem;
  color: var(--surfaces-on-surface);

  .lside {
    flex-shrink: 0;
    width: 200px;
    height: 200px;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 25px;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      position: relative;
      z-index: 1;
    }

    &:after {
      content: '\e81f';
      font-family: 'fontello';
      position: absolute;
      inset: 0;
      text-align: center;
      display: block;
      font-size: 15rem;
      line-height: 20rem;
      color: var(--surfaces-outline);
      filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25));
      opacity: 0.2;
    }
  }

  .rside {
    width: 100%;

    &__header {
      margin-bottom: 20px;
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 20px;
      width: 100%;

      h1 {
        font-size: 28px;
        font-weight: 600;
        line-height: 36px;
        color: var(--surfaces-on-surface);
      }

    }

    .socials {
      display: flex;
      gap: 12px;

      .btn {
        padding-left: 9px;
        padding-right: 9px;
        color: var(--surfaces-on-surface-variable);

        &:hover {
          color: var(--accent-on-accent-secondary);
        }
      }
    }

    .desc {
      font-size: 16px;
      font-weight: 400;
      line-height: 32px;
      max-width: 729px;
      color: var(--surfaces-on-surface-variable);
    }
  }

  @media screen and (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 16px;

    .lside {
      width: 200px;
      height: 200px;
    }
    .rside {
      &__header {
        margin-bottom: 12px;

        h1 {
          font-size: 24px;
          line-height: 32px;
        }
      }

      .desc {
        font-size: 14px;
        line-height: 28px;
        margin-bottom: 24px;

        .btn-link {
          display: inline-block;
          color: var(--surfaces-on-surface);
          font-size: 14px;
          font-weight: 600;
          line-height: 18px;
          text-decoration: underline;
          padding: 0;
          margin-left: 8px;
          position: relative;
          bottom: 2px;
        }
      }
    }

  }
}
</style>
